import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import ArrowsButtonDownMin from '@/components/atom/icon/ArrowsButtonDownMin';
import CareerBadge from '@/components/atom/icon/doctor/CareerBadge';
import RepresentativeCareerBadge from '@/components/atom/icon/doctor/RepresentativeCareerBadge';
import KakaoMap from '@/components/common/KakaoMap';
import DoctorBadges from '@/components/templates/doctor/detail/components/DoctorIntroduction/DoctorBadges';
import DoctorNotice from '@/components/templates/doctor/detail/components/DoctorIntroduction/DoctorNotice';
import Section from '@/components/templates/doctor/detail/components/Section/Section';
import {
  DoctorDetailTabType,
  LoadedDoctorDetailViewModel,
} from '@/components/templates/doctor/detail/useDoctorDetailViewModel';
import theme from '@/styles/theme';

import DoctorOperationTimeTable from './DoctorOperationTimeTable';
import DoctorTreatments from './DoctorTreatments';

interface Props {
  doctorDetailViewModel: LoadedDoctorDetailViewModel;
}

const IMAGE_SHOW_MORE_BUTTON_HEIGHT = 56;
const IMAGE_SHOW_MIN_HEIGHT = 280;
const DoctorIntroduction = ({ doctorDetailViewModel }: Props) => {
  const doctorDetailImageRef = useRef<HTMLImageElement>(null);
  const [isShowMoreImageButton, setIsShowMoreImageButton] = useState(false);

  useEffect(() => {
    if (!doctorDetailImageRef?.current) return;
    if (doctorDetailImageRef.current.clientHeight > IMAGE_SHOW_MIN_HEIGHT) {
      setIsShowMoreImageButton(true);
    }
  }, [doctorDetailImageRef, doctorDetailImageRef.current?.clientHeight]);

  return (
    <>
      <AnchorArea id={DoctorDetailTabType.DoctorProfile} />
      <Wrapper>
        {doctorDetailViewModel.hasNoNotice() ? (
          <></>
        ) : (
          <>
            <DoctorNotice notice={doctorDetailViewModel.doctorDetail.notice} />
            <Section.Divider />
          </>
        )}
        {doctorDetailViewModel.hasNoBadge() ? (
          <></>
        ) : (
          <>
            <DoctorBadges doctorBadges={doctorDetailViewModel.doctorDetail.badges} />
            <Section.Divider />
          </>
        )}
        {doctorDetailViewModel.hasNoCareer() ? (
          <></>
        ) : (
          <>
            <Section>
              <Section.Title title={'경력'} />
              <CareerList>
                {doctorDetailViewModel.doctorDetail.doctor.career.map((career) => (
                  <Career key={`career-${career.id}`}>
                    {career.isRepresentative ? <RepresentativeCareerBadge /> : <CareerBadge />}
                    {doctorDetailViewModel.getCareerDisplayText(career)}
                  </Career>
                ))}
              </CareerList>
            </Section>
            <Section.Divider />
          </>
        )}
        <DoctorSection>
          <Section.Title title={'의사 소개'} />
          <IntroduceWrapper>
            <SimpleIntroduce>{doctorDetailViewModel.doctorDetail.doctor.simpleIntroduce}</SimpleIntroduce>
            <Introduce>{doctorDetailViewModel.doctorDetail.doctor.introduce}</Introduce>
          </IntroduceWrapper>
        </DoctorSection>
        <ImageWrapper isOpen={!isShowMoreImageButton}>
          <Images>
            {doctorDetailViewModel.doctorDetail.doctor.detailImageUrls.map((url) => (
              <Image ref={doctorDetailImageRef} alt={'의사 상세 이미지'} key={`doctor-detail-${url}`} src={url} />
            ))}
          </Images>
          {isShowMoreImageButton && (
            <>
              <ImageGradient />
              <ImageShowMoreButton onClick={() => setIsShowMoreImageButton(false)}>
                더 보기
                <ArrowsButtonDownMin color={theme.color.gray600} />
              </ImageShowMoreButton>
            </>
          )}
        </ImageWrapper>
        <Section.Divider />

        <DoctorTreatments doctorTreatments={doctorDetailViewModel.getDoctorTreatments()} />
        <Section.Divider />

        <Section>
          <Section.Title title={`${doctorDetailViewModel.diagnosisContactType} 진료 시간`} />
          <DoctorOperationTimeTable
            duties={doctorDetailViewModel.getDoctorDuties()}
            todayDuty={doctorDetailViewModel.doctorDetail.doctor.todayDuty}
          />
        </Section>
        <Section.Divider />

        <Section>
          <Section.Title title={'병원 위치'} />
          <Hospital>
            <HospitalInfo>
              <HospitalName>{doctorDetailViewModel.doctorDetail.hospital.name}</HospitalName>
              <HospitalAddress>{`${doctorDetailViewModel.doctorDetail.hospital.roadAddress}, ${doctorDetailViewModel.doctorDetail.hospital.detailAddress}`}</HospitalAddress>
            </HospitalInfo>
            <HospitalCallButton onClick={doctorDetailViewModel.callHospital}>전화</HospitalCallButton>
          </Hospital>
          <StyledMap
            onClick={doctorDetailViewModel.openMap}
            latitude={Number(doctorDetailViewModel.doctorDetail.hospital.latitude)}
            longitude={Number(doctorDetailViewModel.doctorDetail.hospital.longitude)}
          />
        </Section>
        <Section.Divider />
      </Wrapper>
    </>
  );
};

export default DoctorIntroduction;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const CareerList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  padding: 0 24px;
`;

const Career = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  align-items: center;

  ${({ theme }) => `
    ${theme.typography.UI14M};
    color: ${theme.color.gray700};
  `}
`;

const SimpleIntroduce = styled.div`
  align-self: stretch;
  margin-bottom: 12px;

  ${({ theme }) => `
    ${theme.typography.UI14SB};
    color: ${theme.color.gray700};
  `}
`;

const Introduce = styled.div`
  align-self: stretch;
  white-space: pre-wrap;
  ${({ theme }) => `
    ${theme.typography.UI14R};
    color: ${theme.color.gray700};
  `}
`;

const DoctorSection = styled(Section)`
  padding-bottom: 20px;
`;

const IntroduceWrapper = styled.div`
  padding: 0 24px;
  overflow-wrap: anywhere;
`;

const ImageWrapper = styled.div<{ isOpen: boolean }>`
  position: relative;
  ${({ isOpen }) => `
    height: ${isOpen ? 'initial' : IMAGE_SHOW_MIN_HEIGHT + IMAGE_SHOW_MORE_BUTTON_HEIGHT}px;
  
  `}
  overflow: hidden;
`;

const Images = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  background: rebeccapurple;
  box-sizing: content-box;
`;

const ImageGradient = styled.div`
  position: absolute;
  height: 40px;
  width: 100%;
  right: 0;
  bottom: ${IMAGE_SHOW_MORE_BUTTON_HEIGHT - 1}px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #ffffff 100%);
`;

const ImageShowMoreButton = styled.button`
  position: absolute;
  bottom: 0;
  width: 100%;
  height: ${IMAGE_SHOW_MORE_BUTTON_HEIGHT - 1}px;
  ${({ theme }) => `
    background: ${theme.color.white};
    border-top: 1px solid ${theme.color.gray50};
  `};
  svg {
    margin-left: 8px;
  }
`;

const Image = styled.img`
  width: 100%;
`;

const Hospital = styled.div`
  padding: 0 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  align-self: stretch;
`;

const HospitalInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 0 0;
  gap: 2px;
`;

const HospitalName = styled.div`
  ${({ theme }) => `
    ${theme.typography.UI15SB}
    color: ${theme.color.gray700}
  `}
`;

const HospitalAddress = styled.div`
  ${({ theme }) => `
    ${theme.typography.UI12R}
    color: ${theme.color.gray300}
  `}
`;

const HospitalCallButton = styled.div`
  display: flex;
  width: 64px;
  height: 40px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  ${({ theme }) => `
    background: ${theme.color.white};
    border: 1px solid ${theme.color.gray100};
    ${theme.typography.UI14SB}
    color: ${theme.color.gray600}
  `}
`;

const StyledMap = styled(KakaoMap)`
  padding: 0 24px;
  width: 100%;
  height: 196px;
`;

const AnchorArea = styled.div`
  height: 108px;
  margin-top: -108px;
  display: block;
  visibility: hidden;
`;
